import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Grid,
  Alert,
  Snackbar,
  InputAdornment,
  Divider,
  Stack,
  Card,
  CardContent,
  FormGroup,
  FormLabel,
  Checkbox,
  FormControlLabel,
  Container,
  Paper,
  Fade,
  Chip,
  Avatar,
  CircularProgress,
  useTheme,
  alpha,
  useMediaQuery
} from '@mui/material';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import StoreIcon from '@mui/icons-material/Store';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import ImageIcon from '@mui/icons-material/Image';
import CategoryIcon from '@mui/icons-material/Category';
import MapIcon from '@mui/icons-material/Map';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { createBranch } from './branchesApi';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchAllFilters } from './branchFiltersApiStore';
import axios from 'axios';
import config from './config';
// Fix for default marker icon
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

const API_BASE_URL = config.API_BASE_URL;



let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

function LocationMarker({ position, setPosition }) {
  const map = useMap();
  
  useMapEvents({
    click(e) {
      setPosition(e.latlng);
    },
  });

  useEffect(() => {
    if (position) {
      map.flyTo(position, map.getZoom());
    }
  }, [position, map]);

  return position === null ? null : (
    <Marker position={position} draggable={true} eventHandlers={{
      dragend: (e) => {
        setPosition(e.target.getLatLng());
      }
    }}>
    </Marker>
  );
}

export default function JoinStoresYe() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phoneNumber: '',
    facebookUrl: '',
    instagramUrl: '',
    description: '',
    deliveryRangeKm: 20,
    isActive: false,
    openingTime: '08:00',
    closingTime: '07:59',
    deliveryFeeType: 'fixedAmount',
    deliveryFeeValue: 1000,
    image: '',
    referralCode: '' // ✅ NEW: Referral Code
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [position, setPosition] = useState({ lat: 15.3694, lng: 44.1910 });
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [filters, setFilters] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState([]);
  
  // ✅ NEW: Cities state for Delivery Cities
  const [cities, setCities] = useState([]);
  const [selectedCityIds, setSelectedCityIds] = useState([]);
  const [loadingCities, setLoadingCities] = useState(true);

  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    fetchAllFilters().then(setFilters).catch(console.error);
    loadCities();
  }, []);

  // ✅ Load all cities
  const loadCities = async () => {
    try {
      setLoadingCities(true);
      const response = await axios.get(`${API_BASE_URL}/cities`);
      const list = (response.data || []).map((c) => ({
        Id: c.Id ?? c.id,
        Name: c.Name ?? c.name,
      }));
      setCities(list);
    } catch (err) {
      console.error('Error loading cities:', err);
    } finally {
      setLoadingCities(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, image: reader.result });
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    if (name === 'phoneNumber') {
      if ((/^\d*$/.test(value)) && value.length <= 9) {
        setFormData({ ...formData, [name]: value });
      }
    } else if (name === 'referralCode') {
      setFormData({ ...formData, [name]: value.toUpperCase() });
    } else {
      setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
    }
  };

  const handleGetCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setPosition({ lat: pos.coords.latitude, lng: pos.coords.longitude });
          setNotification({ open: true, message: t('join.locationDetected', 'Location detected'), severity: 'success' });
        },
        (err) => {
          console.error(err);
          setNotification({ open: true, message: t('join.errorGettingLocation', 'Error getting location'), severity: 'error' });
        }
      );
    } else {
      setNotification({ open: true, message: t('join.geolocationNotSupported', 'Geolocation not supported'), severity: 'error' });
    }
  };

  const handleFilterChange = (id) => {
    setSelectedFilters((prev) =>
      prev.includes(id) ? prev.filter(fid => fid !== id) : [...prev, id]
    );
  };

  // ✅ NEW: City selection handlers
  const handleCityToggle = (id) => {
    setSelectedCityIds((prev) =>
      prev.includes(id) ? prev.filter((cid) => cid !== id) : [...prev, id]
    );
  };

  const handleSelectAllCities = () => {
    if (selectedCityIds.length === cities.length) {
      setSelectedCityIds([]);
    } else {
      setSelectedCityIds(cities.map((c) => c.Id));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setNotification({ open: true, message: t('join.nameRequired', 'Store name required'), severity: 'error' });
      return;
    }
    if (formData.phoneNumber.length !== 9) {
      setNotification({ open: true, message: t('join.phoneRequired', 'Phone must be 9 digits'), severity: 'error' });
      return;
    }
    if (!formData.address.trim()) {
      setNotification({ open: true, message: t('join.addressRequired', 'Address required'), severity: 'error' });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        address: formData.address,
        phoneNumber: `+967${formData.phoneNumber}`,
        facebookUrl: formData.facebookUrl,
        instagramUrl: formData.instagramUrl,
        description: formData.description,
        image: formData.image,
        latitude: position.lat,
        longitude: position.lng,
        filterIds: selectedFilters,
        cityIds: selectedCityIds,           // ✅ Cities assigned during creation
        isActive: false,                    // ✅ Inactive until admin reviews
        isNew: true,                        // ✅ Flagged as NEW
        openingTime: '08:00',
        closingTime: '07:59',
        deliveryRangeKm: 20,
        deliveryFeeType: 'fixedAmount',
        deliveryFeeValue: 1000,
        referralNumber: formData.referralCode || null // ✅ Referral code support
      };
      await createBranch(payload);
      setShowSuccess(true);
      setTimeout(() => navigate('/select-branch'), 4000);
    } catch (error) {
      console.error('Error creating store:', error);
      setNotification({ open: true, message: t('join.storeCreateFail', 'Failed to create store'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const allCitiesSelected = cities.length > 0 && selectedCityIds.length === cities.length;

  if (showSuccess) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Container maxWidth="md">
          <Fade in={showSuccess}>
            <Card elevation={0} sx={{ p: { xs: 4, md: 6 }, textAlign: 'center', borderRadius: 5, border: `2px solid ${theme.palette.success.main}`, background: `linear-gradient(135deg, ${alpha(theme.palette.success.light, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)` }}>
              <CheckCircleIcon sx={{ fontSize: 120, mb: 3, color: 'success.main' }} />
              <Typography variant="h3" fontWeight={700} color="text.primary" gutterBottom>{t('join.successTitle', 'Store Registered!')}</Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>{t('join.successMessage', 'Your application has been submitted. We will review and contact you soon.')}</Typography>
              <Paper elevation={0} sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), p: 3, borderRadius: 3, border: `1px solid ${alpha(theme.palette.success.main, 0.3)}` }}>
                <Typography variant="body1" color="text.primary" sx={{ mb: 2, fontWeight: 600 }}>{t('join.successContact', 'Our team will contact you within 24 hours.')}</Typography>
                <Typography variant="h6" fontWeight={700} color="success.main">{t('join.redirecting', 'Redirecting...')}</Typography>
              </Paper>
            </Card>
          </Fade>
        </Container>
      </Box>
    );
  }

  const formContent = (
    <Stack spacing={4}>
      {/* Store Information Card */}
      <Card>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 50, height: 50 }}><StoreIcon /></Avatar>
            <Typography variant="h6" fontWeight={700} color="text.primary">{t('join.storeInformation', 'Store Information')}</Typography>
          </Box>
          
          <Stack spacing={3}>
            <TextField fullWidth label={t('join.storeName', 'Store Name')} name="name" value={formData.name} onChange={handleChange} required placeholder={t('join.storeNamePlaceholder', 'Enter your store name')} InputProps={{ startAdornment: <InputAdornment position="start"><StoreIcon color="action" /></InputAdornment> }} />
            <TextField fullWidth label={t('join.phoneNumber', 'Phone Number')} name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} required placeholder="77xxxxxxx" InputProps={{ startAdornment: <InputAdornment position="start"><Chip icon={<PhoneIcon sx={{ fontSize: 16 }} />} label="+967" size="small" sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 700, mr: 1 }} /></InputAdornment> }} helperText={`${formData.phoneNumber.length}/9 ${t('join.digits', 'digits')}`} error={formData.phoneNumber.length > 0 && formData.phoneNumber.length !== 9} />
            <TextField fullWidth label={t('join.address', 'Address')} name="address" value={formData.address} onChange={handleChange} required multiline rows={3} placeholder={t('join.addressPlaceholder', 'Enter your store address')} InputProps={{ startAdornment: <InputAdornment position="start"><LocationOnIcon color="action" /></InputAdornment> }} />
            <TextField fullWidth label={t('join.description', 'Description')} name="description" value={formData.description} onChange={handleChange} multiline rows={3} placeholder={t('join.descriptionPlaceholder', 'Describe your store...')} />
            
            <Box>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><ImageIcon color="primary" />{t('join.storeImage', 'Store Image')}</Typography>
              <Button variant="outlined" component="label" fullWidth size="large" startIcon={<ImageIcon />} sx={{ py: 2.5, borderStyle: 'dashed', borderWidth: 2, borderColor: 'primary.main', color: 'primary.main', fontWeight: 700, '&:hover': { borderStyle: 'dashed', bgcolor: alpha(theme.palette.primary.main, 0.05) } }}>
                {t('join.uploadImage', 'Upload Image')}
                <input type="file" accept="image/*" hidden onChange={handleImageChange} />
              </Button>
              {imagePreview && (
                <Box mt={2} sx={{ textAlign: 'center', p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 3, border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}` }}>
                  <img src={imagePreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: 200, borderRadius: theme.shape.borderRadius, boxShadow: theme.shadows[4] }} />
                </Box>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* ✅ NEW: Referral Code Card */}
      <Card>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
            <Avatar sx={{ bgcolor: '#FF6B35', width: 45, height: 45 }}><CardGiftcardIcon /></Avatar>
            <Typography variant="h6" fontWeight={700} color="text.primary">{t('join.referralCode', 'Referral Code (Optional)')}</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('join.referralHint', 'If someone referred you, enter their code here')}
          </Typography>
          <TextField 
            fullWidth 
            label={t('join.referralCodeLabel', 'Referral Code')} 
            name="referralCode" 
            value={formData.referralCode} 
            onChange={handleChange} 
            placeholder={t('join.referralPlaceholder', 'Enter referral code (e.g., REF123ABC)')} 
            inputProps={{ maxLength: 10, style: { textTransform: 'uppercase' } }}
          />
        </CardContent>
      </Card>

      {/* Social Media Card */}
      <Card>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>{t('join.socialMedia', 'Social Media')}</Typography>
          <Stack spacing={2}>
            <TextField fullWidth label="Facebook" name="facebookUrl" value={formData.facebookUrl} onChange={handleChange} InputProps={{ startAdornment: <InputAdornment position="start"><FacebookIcon sx={{ color: '#1877F2' }} /></InputAdornment> }} placeholder="https://facebook.com/yourstore" />
            <TextField fullWidth label="Instagram" name="instagramUrl" value={formData.instagramUrl} onChange={handleChange} InputProps={{ startAdornment: <InputAdornment position="start"><InstagramIcon sx={{ color: '#E4405F' }} /></InputAdornment> }} placeholder="https://instagram.com/yourstore" />
          </Stack>
        </CardContent>
      </Card>

      {/* ✅ NEW: Delivery Cities Card */}
      <Card>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
            <Avatar sx={{ bgcolor: '#2e7d32', width: 45, height: 45 }}><MapIcon /></Avatar>
            <Typography variant="h6" fontWeight={700} color="text.primary">{t('join.deliveryCities', 'Delivery Cities')}</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('join.deliveryCitiesHint', 'Select the cities where your store will deliver')}
          </Typography>

          {loadingCities ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4, gap: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">{t('join.loadingCities', 'Loading cities...')}</Typography>
            </Box>
          ) : cities.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">{t('join.noCitiesAvailable', 'No cities available')}</Typography>
            </Box>
          ) : (
            <>
              <Button
                variant={allCitiesSelected ? "contained" : "outlined"}
                color="success"
                onClick={handleSelectAllCities}
                startIcon={<DoneAllIcon />}
                fullWidth
                sx={{ mb: 3, py: 1.5, fontWeight: 700 }}
              >
                {allCitiesSelected ? t('join.clearAll', 'Clear All') : t('join.selectAll', 'Select All')} ({selectedCityIds.length}/{cities.length})
              </Button>

              <Grid container spacing={1.5}>
                {cities.map((city) => {
                  const sel = selectedCityIds.includes(city.Id);
                  return (
                    <Grid item xs={6} sm={4} key={city.Id}>
                      <Paper
                        elevation={0}
                        onClick={() => handleCityToggle(city.Id)}
                        sx={{
                          p: 1.5,
                          textAlign: 'center',
                          borderRadius: 2,
                          cursor: 'pointer',
                          border: `1.5px solid ${sel ? theme.palette.success.main : theme.palette.divider}`,
                          bgcolor: sel ? alpha(theme.palette.success.main, 0.1) : 'background.paper',
                          color: sel ? 'success.dark' : 'text.primary',
                          fontWeight: sel ? 700 : 500,
                          transition: 'all 0.2s',
                          '&:hover': { borderColor: 'success.main' }
                        }}
                      >
                        {city.Name}
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </>
          )}
        </CardContent>
      </Card>

      {/* Categories / Filters Card */}
      {filters.length > 0 && (
        <Card>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <FormLabel component="legend" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}><CategoryIcon color="primary" />{t('join.storeCategories', 'Store Categories')}</FormLabel>
            <Paper elevation={0} sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.03), border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`, borderRadius: 3 }}>
              <FormGroup row>
                {filters.map((filter) => (
                  <FormControlLabel key={filter.Id} control={<Checkbox checked={selectedFilters.includes(filter.Id)} onChange={() => handleFilterChange(filter.Id)} color="primary" />} label={filter.name} sx={{ minWidth: '50%', mb: 1 }} />
                ))}
              </FormGroup>
            </Paper>
          </CardContent>
        </Card>
      )}

      {/* Submit Button Card */}
      <Card elevation={0} sx={{ p: 4, textAlign: 'center', bgcolor: alpha(theme.palette.primary.main, 0.05), border: `2px solid ${theme.palette.primary.main}`, borderRadius: 5 }}>
        <Button 
          type="submit" 
          variant="contained" 
          size="large" 
          disabled={loading || formData.phoneNumber.length !== 9 || !formData.name.trim()} 
          startIcon={loading ? null : <RocketLaunchIcon />} 
          sx={{ px: 8, py: 2, fontSize: '1.2rem', fontWeight: 700, boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`, '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.4)}` } }}
        >
          {loading ? t('join.submitting', 'Submitting...') : t('join.submitStore', 'Submit Your Store')}
        </Button>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontWeight: 600 }}>{t('join.submitDisclaimer', 'Your store will be reviewed before activation. Usually takes 24-48 hours.')}</Typography>
      </Card>
    </Stack>
  );

  const mapComponent = (
    <Card sx={{ height: isMobile ? 500 : '100%', minHeight: isMobile ? 500 : 600 }}>
      <CardContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 3, bgcolor: 'primary.main', color: 'white' }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>{t('join.pinYourLocation', 'Pin Your Location')}</Typography>
          <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>{t('join.mapInstruction', 'Tap on the map to set your store location')}</Typography>
          <Button variant="contained" fullWidth size="large" startIcon={<MyLocationIcon />} onClick={handleGetCurrentLocation} sx={{ bgcolor: 'white', color: 'primary.main', fontWeight: 700, '&:hover': { bgcolor: alpha(theme.palette.common.white, 0.9) } }}>
            {t('join.useMyLocation', 'Use My Location')}
          </Button>
        </Box>
        
        <Box sx={{ flexGrow: 1, position: 'relative' }}>
          <MapContainer center={position} zoom={13} style={{ height: '100%', width: '100%' }} attributionControl={false}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <LocationMarker position={position} setPosition={setPosition} />
          </MapContainer>
        </Box>

        <Box sx={{ p: 2, bgcolor: 'background.default', borderTop: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom fontWeight={600}>{t('join.selectedCoordinates', 'Selected Coordinates')}</Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}><TextField fullWidth size="small" label={t('join.latitude', 'Latitude')} value={position.lat.toFixed(6)} InputProps={{ readOnly: true }} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label={t('join.longitude', 'Longitude')} value={position.lng.toFixed(6)} InputProps={{ readOnly: true }} /></Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: { xs: 3, md: 6 } }}>
      <Container maxWidth="xl">
        {/* Header Hero */}
        <Paper elevation={0} sx={{ p: { xs: 3, md: 6 }, mb: 4, textAlign: 'center', bgcolor: 'background.paper', borderRadius: 5, border: `1px solid ${theme.palette.divider}`, boxShadow: '0 8px 24px rgba(2, 55, 68, 0.08)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 80, height: 80, boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}` }}>
              <RocketLaunchIcon sx={{ fontSize: 45 }} />
            </Avatar>
          </Box>
          <Typography variant="h3" fontWeight={700} color="text.primary" gutterBottom sx={{ mb: 2, fontSize: { xs: '1.75rem', md: '2.5rem' } }}>
            {t('join.heroTitle', 'Join StoresYe Today!')}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4, maxWidth: 700, mx: 'auto', fontSize: { xs: '0.95rem', md: '1.25rem' } }}>
            {t('join.heroSubtitle', 'Register your store and expand your reach')}
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap>
            <Chip icon={<LocalShippingIcon />} label={t('join.benefit1', 'Fast Delivery')} color="primary" size="large" sx={{ fontWeight: 700, py: 3, px: 2 }} />
            <Chip icon={<StoreIcon />} label={t('join.benefit2', 'Online Store')} color="secondary" size="large" sx={{ fontWeight: 700, py: 3, px: 2 }} />
            <Chip icon={<CheckCircleIcon />} label={t('join.benefit3', 'Easy Setup')} color="success" size="large" sx={{ fontWeight: 700, py: 3, px: 2 }} />
          </Stack>
        </Paper>

        {isMobile ? (
          <form onSubmit={handleSubmit}>
            <Stack spacing={4}>
              {formContent}
              {mapComponent}
            </Stack>
          </form>
        ) : (
          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
              <Box sx={{ flex: '0 0 50%', maxWidth: '50%' }}>
                {formContent}
              </Box>
              <Box sx={{ flex: '0 0 50%', maxWidth: '50%', position: 'sticky', top: 100, height: 'calc(100vh - 120px)' }}>
                {mapComponent}
              </Box>
            </Box>
          </form>
        )}
      </Container>

      <Snackbar open={notification.open} autoHideDuration={6000} onClose={handleCloseNotification} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseNotification} severity={notification.severity} variant="filled" sx={{ width: '100%', fontSize: '1rem', fontWeight: 700 }}>{notification.message}</Alert>
      </Snackbar>
    </Box>
  );
}